/**
 * Row density for the calendar grids.
 *
 * A fixed row height suits either a handful of calendars or a crowded one,
 * never both: 30px rows waste the screen with four people and 60px rows push
 * a team of twenty off the bottom. The choice is the user's, and it sticks.
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'beeplanif.rowDensity';

/**
 * `lane` is the timeline row height (FullCalendar `resourceLaneHeight`);
 * `slot` is the hour-grid slot height, applied through a CSS variable.
 */
export const DENSITIES = {
  compact: { label: 'Compacte', lane: 24, slot: 18 },
  normal: { label: 'Normale', lane: 36, slot: 24 },
  comfortable: { label: 'Confortable', lane: 52, slot: 34 },
};

export const DEFAULT_DENSITY = 'normal';

function readStored() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && DENSITIES[stored] ? stored : DEFAULT_DENSITY;
  } catch {
    // Private browsing, or storage blocked: fall back rather than fail.
    return DEFAULT_DENSITY;
  }
}

/**
 * Current density plus a setter that persists it.
 *
 * Both heights are pushed to the document as CSS variables: FullCalendar has
 * no option for either. `resourceLaneHeight` looks like the one for timelines
 * but is not a real option — it is rejected as "Unknown option" and does
 * nothing. Both grids are sized in CSS.
 */
export function useRowDensity() {
  const [density, setDensityState] = useState(readStored);

  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--planif-slot-height', `${DENSITIES[density].slot}px`);
    root.setProperty('--planif-lane-height', `${DENSITIES[density].lane}px`);
  }, [density]);

  const setDensity = useCallback((next) => {
    if (!DENSITIES[next]) return;
    setDensityState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisting is survivable; the session still honours the choice.
    }
  }, []);

  return { density, setDensity, lane: DENSITIES[density].lane };
}
